import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getTimeline } from "@/lib/momentum.functions";
import { useBusiness } from "@/components/business-context";
import { CATEGORIES, formatDay, formatMonth } from "@/lib/momentum-types";
import { getAccount } from "@/lib/account.functions";
import { planOf } from "@/lib/plans";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/history")({
  head: () => ({
    meta: [
      { title: "History — The Founders App" },
      {
        name: "description",
        content:
          "One chronological timeline of daily moves, bold moves, journal entries and outreach.",
      },
      { property: "og:title", content: "History — The Founders App" },
      { property: "og:description", content: "Scroll back and spot the patterns." },
    ],
  }),
  component: HistoryPage,
});

const KINDS = [
  { value: "all", label: "Everything" },
  { value: "Daily", label: "Daily moves" },
  { value: "Monthly risk", label: "Bold moves" },
  { value: "Journal", label: "Journal" },
  { value: "Contact", label: "Outreach" },
];

interface Item {
  key: string;
  at: string;
  when: string;
  label: string;
  category?: string | null;
  title?: string | null;
  body?: string | null;
}

function HistoryPage() {
  const { active } = useBusiness();
  const businessId = active?.id ?? "";
  const fetchTimeline = useServerFn(getTimeline);
  const account = useQuery({ queryKey: ["account"], queryFn: () => getAccount() });
  const plan = planOf(account.data?.plan.code);
  const [kind, setKind] = useState<string>("all");
  const [category, setCategory] = useState<string>("all");

  const query = useQuery({
    queryKey: ["timeline", businessId],
    enabled: Boolean(businessId),
    queryFn: () => fetchTimeline({ data: { businessId } }),
  });

  if (!businessId) return null;

  const data = query.data;
  const items: Item[] = [];

  if (data) {
    const contactName = new Map(data.contacts.map((c) => [c.id, c.name]));
    for (const e of data.daily) {
      items.push({
        key: `daily-${e.id}`,
        at: e.entry_date,
        when: formatDay(e.entry_date),
        label: "Daily",
        category: e.category,
        title: e.morning_text,
        body: e.evening_text,
      });
    }
    for (const m of data.bold) {
      items.push({
        key: `bold-${m.id}`,
        at: m.month,
        when: formatMonth(m.month),
        label: "Monthly risk",
        title: m.text,
      });
    }
    for (const j of data.journal) {
      items.push({
        key: `journal-${j.id}`,
        at: j.created_at,
        when: formatDay(j.created_at),
        label: "Journal",
        body: j.body,
      });
    }
    for (const i of data.interactions) {
      items.push({
        key: `interaction-${i.id}`,
        at: i.occurred_on,
        when: formatDay(i.occurred_on),
        label: "Contact",
        title: contactName.get(i.contact_id) ?? "Contact",
        body: i.note,
      });
    }
    items.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  }

  const visible = plan.filters
    ? items.filter(
        (i) =>
          (kind === "all" || i.label === kind) &&
          (category === "all" || i.category === category),
      )
    : items;

  return (
    <div className="space-y-6">
      <div>
        <p className="micro-label">History</p>
        <h2 className="mt-2 text-xl font-medium">Everything, in order</h2>
      </div>

      {plan.filters ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {KINDS.map((k) => (
              <button
                key={k.value}
                onClick={() => setKind(k.value)}
                className={cn(
                  "rounded-sm border px-3 py-1.5 text-[0.7rem] transition-colors",
                  kind === k.value
                    ? "border-foreground/40 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {k.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[{ value: "all", label: "Any category" }, ...CATEGORIES].map((c) => (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                className={cn(
                  "rounded-sm px-2.5 py-1 text-[0.7rem] transition-colors",
                  category === c.value
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="soft-panel px-4 py-3 text-xs text-muted-foreground">
          Category tagging and timeline filters come with{" "}
          <Link to="/pricing" className="text-foreground underline underline-offset-4">
            Operator
          </Link>
          .
        </p>
      )}
      {visible.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing to look back on yet. Log today's move and the timeline starts here.
        </p>
      )}
      <ol className="relative space-y-6 border-l border-border pl-6">
        {visible.map((item) => (
          <li key={item.key} className="relative">
            <span className="absolute top-2 -left-[1.72rem] h-2 w-2 rounded-full bg-primary" />
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-muted-foreground">{item.when}</span>
              <span className="rounded-full border border-border px-2 py-0.5 text-[0.7rem] text-muted-foreground">
                {item.label}
              </span>
              {item.category && (
                <span className="text-[0.7rem] text-muted-foreground">
                  {CATEGORIES.find((c) => c.value === item.category)?.label ?? item.category}
                </span>
              )}
            </div>
            {item.title && <p className="notranslate mt-2 font-medium">{item.title}</p>}
            {item.body && (
              <p className="notranslate mt-1 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {item.body}
              </p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
